import { useState } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { useApplyPlanMutation, useApplyPreviewQuery } from '../../queries/mediaPlanning';
import { formatMoneyCell } from './mediaPlanningView';

const SUPPORTED_PLATFORMS = new Set(['meta_ads', 'google_ads']);
const todayIso = () => new Date().toISOString().slice(0, 10);
const ddmm = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

/**
 * "Aplicar plan de hoy" (F2): opens a confirm dialog with the dry-run diff of
 * what would change on the platform, then pushes the real budgets on confirm.
 */
export default function ApplyPlanBar({ brandId, month }) {
  const [open, setOpen] = useState(false);
  const date = todayIso();
  const preview = useApplyPreviewQuery(brandId, date, { enabled: open });
  const applyMutation = useApplyPlanMutation(brandId, month);

  const actions = preview.data?.actions ?? [];
  const displayCurrency = preview.data?.currency;
  const nativeFmt = (v) => v.toLocaleString('es-ES', { maximumFractionDigits: 2 });

  const confirm = async () => {
    try {
      const res = await applyMutation.mutateAsync(date);
      const results = res.results || [];
      const failed = results.filter((r) => r.status === 'failed');
      const skipped = results.filter((r) => r.status === 'unsupported' || r.status === 'shared_skipped');
      if (failed.length) {
        toast.error(`Aplicado con ${failed.length} error(es)`);
      } else if (skipped.length && res.applied === 0) {
        toast('No se aplicaron cambios (presupuestos compartidos o no disponibles).', { icon: 'ℹ️' });
      } else {
        const extra = skipped.length ? ` · ${skipped.length} omitido(s)` : '';
        toast.success(`Plan aplicado: ${res.applied} cambio(s)${extra}`);
      }
      setOpen(false);
    } catch (_e) {
      toast.error('No se pudo aplicar el plan');
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90"
      >
        Aplicar plan de hoy ({ddmm(date)})
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-card bg-white p-5 shadow-xl">
            <h3 className="text-base font-semibold text-gray-900">Aplicar plan del {ddmm(date)}</h3>
            <p className="mt-1 text-sm text-gray-500">
              Se ajustará el presupuesto diario de estas campañas en la plataforma.
            </p>

            <div className="my-4 max-h-72 overflow-y-auto">
              {preview.isLoading ? (
                <p className="py-6 text-center text-sm text-gray-500">Calculando cambios…</p>
              ) : preview.isError ? (
                <p className="py-6 text-center text-sm text-rose-500">No se pudo calcular el diff.</p>
              ) : actions.length === 0 ? (
                <p className="py-6 text-center text-sm text-gray-500">
                  Sin cambios: la plataforma ya coincide con el plan.
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {actions.map((a) => {
                    const blocked = !SUPPORTED_PLATFORMS.has(a.platform) || a.shared;
                    const blockedLabel = a.shared ? 'presupuesto compartido' : 'no disponible';
                    const cross = a.currency && a.currency !== displayCurrency;
                    const dtn = a.display_to_native || 1;
                    return (
                      <li key={`${a.platform}:${a.campaign_id}:${a.group_id ?? ''}`} className="flex items-start justify-between gap-3 py-2 text-sm">
                        <span className={`truncate ${blocked ? 'text-gray-400' : 'text-gray-700'}`}>
                          {a.group_name ? (
                            <>
                              <span className="text-gray-400">{a.campaign_name || a.campaign_id} › </span>
                              {a.group_name}
                            </>
                          ) : (
                            a.campaign_name || a.campaign_id
                          )}
                          {blocked && <span className="ml-1 text-[11px] text-gray-400">· {blockedLabel}</span>}
                        </span>
                        <span className="whitespace-nowrap text-right">
                          <span className="tabular-nums text-gray-500">
                            {formatMoneyCell(a.from_amount)} <span className="text-gray-400">→</span>{' '}
                            <strong className={`font-medium ${blocked ? 'text-gray-400' : 'text-amber-700'}`}>
                              {formatMoneyCell(a.to_amount)}
                            </strong>
                          </span>
                          {cross && (
                            <span className="block text-[11px] tabular-nums text-gray-400">
                              en {a.currency}: {nativeFmt(a.from_amount * dtn)} → {nativeFmt(a.to_amount * dtn)}
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirm}
                disabled={applyMutation.isPending || actions.length === 0}
                className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
              >
                {applyMutation.isPending ? 'Aplicando…' : 'Confirmar y aplicar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

ApplyPlanBar.propTypes = {
  brandId: PropTypes.number.isRequired,
  month: PropTypes.string.isRequired,
};
